import { persistState, recoverState } from './indexed-db';
import { INITIAL_STATE } from '../reducers';
import { processFolders, handleRackendRequestReset as handleRackendRequestResetFolder } from './folder';
import { handleRackendRequestReset as handleRackendRequestResetMessage } from './message';
import { handleRackendRequestReset, handleRefreshMessageBackendRequestReset } from './application';
import Cookies from 'js-cookie';

export const KEY_USER_ID = 'KEY_USER_ID';
export const KEY_HASH = 'KEY_HASH';
export const LEXON = 'LEXON';
export const CURRENT_USER = 'CURRENT_USER';
const TAB_INITIALIZED = 'TAB_INITIALIZED';
export const SET_FIRST_SYNC_IMAP = 'SET_FIRST_SYNC_IMAP';
const COOKIE_PREFIXES_TO_PRESERVE = [
  'CookieConsent',
  'Lefebvre.OfficeConfigHoursCalendar',
  'Lefebvre.GmailConfigHoursCalendar',
  'Lefebvre.ImapConfigHoursCalendar'
];

/**
 * Elimina todas las cookies legibles del dominio .lefebvre.es al cerrar sesión
 * o al cambiar de cuenta, excepto las de consentimiento de Cookiebot
 * (CookieConsent*), que deben preservarse entre sesiones.
 * 
 * Esta función es exportada para ser usada tanto en el proceso de logout
 * como al crear nuevas cuentas por defecto.
 */
export const removeAllDefaultAccountCookies = () => {
  const allCookies = Cookies.get();
  const domain = '.lefebvre.es';
  
  Object.keys(allCookies).forEach(cookieName => {
      // No borrar el consentimiento de Cookiebot: sin esta cookie el modal
      // de aceptar cookies vuelve a saltar en cada logout / cambio de cuenta
      if (COOKIE_PREFIXES_TO_PRESERVE.some(prefix => cookieName.startsWith(prefix))) {
        return;
      }
      // Eliminar la cookie con el dominio especificado
      Cookies.remove(cookieName, { domain });
      // También intentar eliminar sin dominio por si acaso
      Cookies.remove(cookieName);
  });
};

function emptyState() {
  return JSON.parse(JSON.stringify(INITIAL_STATE));
}

/**
 * Loads the Redux Store state back from the Browser's IndexedDB.
 *
 * It first checks the Browser's session storage to retrieve the hashed user id and the hashed user credentials.
 *
 * Hashed user id will be used as the key in the IndexedDB and the credential hash as the cypher password.
 *
 * @returns {Promise<*>}
 */
export async function loadState() {
  const state = emptyState();
  // Per-tab flag to detect a fresh tab/window load
  const isFirstLoadThisTab = sessionStorage.getItem(TAB_INITIALIZED) !== '1';
  const userId = localStorage.getItem(KEY_USER_ID);
  const hash = localStorage.getItem(KEY_HASH);
  if (userId !== null && hash !== null) {
    const dbState = await recoverState(userId, hash);
    if (dbState && dbState.application && dbState.folders && dbState.messages) {
      state.application = { ...dbState.application };
      state.folders.items = [...dbState.folders.items];
      state.folders.explodedItems = { ...dbState.folders.explodedItems };
      processFolders(Object.values(state.folders.explodedItems));
      state.login = { ...dbState.login };
      state.messages.cache = { ...dbState.messages.cache };
      // Do not carry over compose draft to a brand-new tab/window
      if (isFirstLoadThisTab) {
        state.application.newMessage = null;
        state.application.selectedMessage = {};
      }
    }
  }

  const lexon = localStorage.getItem(LEXON);
  if (lexon !== null) {
    state.lexon = { ...state.lexon, ...JSON.parse(lexon) };
  }

  const currentUser = localStorage.getItem(CURRENT_USER);
  if (currentUser !== null && currentUser.user !== null) {
    state.currentUser = JSON.parse(currentUser);
  }

  // Mark this tab as initialized so subsequent reloads can keep the draft
  sessionStorage.setItem(TAB_INITIALIZED, '1');
  const setFirstSyncImap = localStorage.getItem(SET_FIRST_SYNC_IMAP);
  if (setFirstSyncImap !== null) {
    state.folders.firstSyncCompleted = JSON.parse(setFirstSyncImap);
  }

  return state;
}

/**
 * Stores the current Redux Store state into the Browser's IndexedDB using an encryption algorithm under a hashed key.
 *
 * In order to be able to retrieve back the state from the indexed db, the hashed user id and the hashed user credentials
 * will be used as key and cypher password.
 *
 * @param dispatch {(Dispatch<any>|function)}
 * @param state
 */
export function saveState(dispatch, state) {
  if (window === window.top) {
    localStorage.setItem(KEY_USER_ID, state.application.user.id);
    localStorage.setItem(KEY_HASH, state.application.user.hash);
  }

  // Don't persist sensitive NextCloud credentials to localStorage
  const lexonToPersist = {
    ...state.lexon,
    nextcloudAccountAuth: undefined
  };
  localStorage.setItem(LEXON, JSON.stringify(lexonToPersist));
  localStorage.setItem(CURRENT_USER, JSON.stringify(state.currentUser));
  localStorage.setItem(SET_FIRST_SYNC_IMAP, JSON.stringify(state.folders.firstSyncCompleted));

  persistState(dispatch, state);
}

export function saveStateStorage(state) {
  if (window === window.top) {
    localStorage.setItem(KEY_USER_ID, state.application.user.id);
    localStorage.setItem(KEY_HASH, state.application.user.hash);
  }

  // Don't persist sensitive NextCloud credentials to localStorage
  const lexonToPersist = {
    ...state.lexon,
    nextcloudAccountAuth: undefined
  };
  localStorage.setItem(LEXON, JSON.stringify(lexonToPersist));
  localStorage.setItem(CURRENT_USER, JSON.stringify(state.currentUser));
  localStorage.setItem(SET_FIRST_SYNC_IMAP, JSON.stringify(state.folders.firstSyncCompleted));
}

export function removeState() {
  // Eliminar todas las cookies de DefaultAccount al cerrar sesión
  removeAllDefaultAccountCookies();
  
  const lexonRaw = localStorage.getItem(LEXON);
  if (lexonRaw) {
    try {
      const { token, lexonToken } = JSON.parse(lexonRaw);
      localStorage.setItem(LEXON, JSON.stringify({ token, lexonToken }));
    } catch {
      localStorage.removeItem(LEXON);
    }
  }

  localStorage.removeItem(KEY_USER_ID);
  localStorage.removeItem(KEY_HASH);
  localStorage.removeItem(CURRENT_USER);
  localStorage.removeItem(SET_FIRST_SYNC_IMAP);
  sessionStorage.clear();
}

export function removeStateExLexon() {
  localStorage.removeItem(KEY_USER_ID);
  localStorage.removeItem(KEY_HASH);
  localStorage.removeItem(CURRENT_USER);
  localStorage.removeItem(SET_FIRST_SYNC_IMAP);
  sessionStorage.clear();
}

export function resetBackendRequestsState(dispatch) {
  handleRackendRequestResetFolder(dispatch);
  handleRackendRequestResetMessage(dispatch);
  handleRackendRequestReset(dispatch);
  handleRefreshMessageBackendRequestReset(dispatch);
}

